import {useCallback, useEffect, useMemo, useState} from 'react'
import axios from 'axios'
import {useReduxAuth} from '../../../auth'
import {toast} from 'react-toastify'
import DataTableList from '../../components/DataTableList'
import {MEDICAL_ASSISTANT_HEADERS} from '../../../../constants/RoleConstants'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import CreateUserFrom from '../../components/forms/CreateUserForm'
import {filterHeadersByUserRole} from '../../helper/helperFunction'
import MetaTags from '../../../../meta/MetaTags'
import Loader from '../../components/Loader'
import {USER_ROLES} from '../../../../constants/StatusConstants'

const MedicalAssistantListingPage = () => {
  // toast alerts
  const notifyError = (msg: string) => toast.error(msg)
  const notifySuccess = (msg: string) => toast.success(msg)

  const [userData, setUserData] = useState<any>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isInviteLoading, setIsInviteLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const {currentUser} = useReduxAuth()

  // function to get data
  const fetchData = useCallback(async () => {
    setIsLoading(true)

    try {
      const response = await axios.get(API_ENDPOINTS.GET_ALL_MEDICAL_ASSISTANTS())
      
      const items = response?.data?.data?.medical_assistants || 
                    response?.data?.data?.medicalAssistants || 
                    response?.data?.medical_assistants ||
                    response?.data?.medicalAssistants ||
                    response?.data?.data || 
                    []
      
      setUserData(Array.isArray(items) ? items : [])
    } catch (error: any) {
      const statusCode = error?.response?.status
      const errorMessage = error?.response?.data?.message || ''
      
      console.error('Error fetching medical assistants:', {
        status: statusCode,
        message: errorMessage,
        response: error?.response?.data
      })
      
      notifyError(errorMessage || 'Failed to load medical assistants')
      setUserData([])
    } finally {
      setIsLoading(false)
    }
  }, [notifyError])

  // fetch data - only once on mount
  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // create new medical assistant
  const handleSubmit = async (values: any, resetForm: () => void) => {
    setIsInviteLoading(true)
    let response
    try {
      
      const endpoint = API_ENDPOINTS.INVITE_USER('medical_assistant', null)
      response = await axios.post(endpoint, {
        email: values.email,
        name: values.name,
        role: 'medical_assistant',
      })
      
      // Show custom success message for Medical Assistant
      notifySuccess('Medical Assistant invited successfully')

      // Fetch updated data and close the modal
      await fetchData()
    } catch (error: any) {
      notifyError(error?.response?.data?.message || 'Failed to invite medical assistant')
    } finally {
      setIsInviteLoading(false)
      setIsModalOpen(false)
      resetForm()
    }
  }

  // Function to filter headers based on user permissions
  const filteredHeaders = filterHeadersByUserRole(MEDICAL_ASSISTANT_HEADERS, currentUser)
  
  // search filters from datatables
  const [searchText, setSearchText] = useState('')
  const filteredData = useMemo(() => {
    return userData.filter((ma: any) => {
      // Search text filter
      const searchFilter =
        searchText === '' ||
        ma.full_name?.toLowerCase().includes(searchText.toLowerCase()) ||
        ma.email?.toLowerCase().includes(searchText.toLowerCase())

      return searchFilter
    })
  }, [userData, searchText])
  
  const handleFilterChange = (key: string, value: string) => {
    switch (key) {
      case 'search':
        setSearchText(value)
        break
      default:
        break
    }
  }
  
  const handleClearAllFilters = () => {
    setSearchText('')
  }

  const customFilters = [
    {
      name: 'Search',
      key: 'search',
      type: 'search' as const,
      placeholder: 'Search by name or email',
      value: searchText,
    },
  ]

  return (
    <>
      <MetaTags title={'Medical Assistant List'} />
      {isInviteLoading ? (
        <Loader />
      ) : (
        <>
          <DataTableList
            title='Medical Assistant'
            columns={filteredHeaders}
            data={filteredData}
            showAddButton={currentUser?.role_id === USER_ROLES.ADMIN}
            onAddClick={() => setIsModalOpen(!isModalOpen)}
            addButtonText="Add Medical Assistant"
            isLoading={isLoading}
            showOnlySearch={true}
            showCustomFilters={true}
            customFilters={customFilters}
            onCustomFilterChange={handleFilterChange}
            onClearAllCustomFilters={handleClearAllFilters}
          />

          {/* create medical assistant form */}
          <CreateUserFrom
            title='Invite Medical Assistant'
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            isLoading={isInviteLoading}
            handleSubmit={handleSubmit}
          />
        </>
      )}
    </>
  )
}

export default MedicalAssistantListingPage

